import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import countries from 'world-countries';

// Localised country options for the address forms.
//
// The value we STORE stays the canonical English common name ("Germany") —
// stable across languages and matching existing booking records. Only the
// LABEL shown to the user is localised (DE: "Deutschland"), so switching the
// UI language never rewrites the saved value. Call sites keep their plain
// string `options` / `value` APIs and translate at the boundary:
//   - `toLabel(value)` — stored English value -> label shown in the field
//   - `toValue(label)` — label the user picked -> English value to store
// so no shared select component needs a value/label split.
export function useCountryOptions() {
    const { i18n } = useTranslation();
    return useMemo(() => {
        const isDe = (i18n.language || 'en').toLowerCase().startsWith('de');
        const labelOf = (c: (typeof countries)[number]): string =>
            (isDe ? c.translations.deu?.common : c.name.common) ?? c.name.common;

        const toLabelMap: Record<string, string> = {}; // English value -> label
        const toValueMap: Record<string, string> = {}; // label -> English value
        for (const c of countries) {
            const value = c.name.common;
            const label = labelOf(c);
            toLabelMap[value] = label;
            toValueMap[label] = value;
        }

        const options = Object.keys(toValueMap).sort((a, b) =>
            a.localeCompare(b, isDe ? 'de' : 'en'));

        return {
            options,
            toLabel: (value: string): string => toLabelMap[value] ?? value,
            toValue: (label: string): string => toValueMap[label] ?? label,
        };
    }, [i18n.language]);
}

// Localised country options keyed by ISO 3166-1 alpha-2 CODE, for surfaces
// whose backend stores the code rather than the English name (the H-3b
// organization billing address: `billing_country VARCHAR(2)`). Shaped for
// `SearchableSelectField`: the label is localised, and `keywords` carries the
// code plus the English name so searching "DE" or "Germany" matches in a
// German UI too.
export interface CountryCodeOption {
    value: string;
    label: string;
    keywords: string;
}

export function useCountryCodeOptions(): CountryCodeOption[] {
    const { i18n } = useTranslation();
    return useMemo(() => {
        const isDe = (i18n.language || 'en').toLowerCase().startsWith('de');
        return countries
            .map((c) => ({
                value: c.cca2,
                label: (isDe ? c.translations.deu?.common : c.name.common) ?? c.name.common,
                keywords: `${c.cca2} ${c.name.common}`,
            }))
            .sort((a, b) => a.label.localeCompare(b.label, isDe ? 'de' : 'en'));
    }, [i18n.language]);
}

export default useCountryOptions;
